// ============================================================
// PINGS - saying something on the map without taking a turn.
//
// A ping is not an action. It writes nothing to a combatant, spends no
// movement and does not touch the turn tracker: it is speech, aimed at a
// point on the ground, and it goes away by itself.
//
// EXPIRY IS A RENDER RULE, NOT A JOB. A ping is drawn only while
// `now - at < PING_TTL_MS`. The client that sent it deletes its own
// document afterwards, and a client that closed its tab leaves one
// behind that every other client already refuses to draw - so there is
// nothing to clean up and no server to do it.
//
// Pure. Zero React dependency.
// ============================================================
import type { Viewport } from './projection';

export type PingType = 'look' | 'danger' | 'going' | 'question';

export interface Ping {
  id: string;
  /**
   * The discriminator inside `sessions/{id}/battlefield`, which holds
   * tokens too. `useBattlefieldSync` already filters that collection by
   * `kind`, which is why pings can live there without a rules change.
   */
  kind: 'ping';
  type: PingType;
  x: number;
  z: number;
  /**
   * How high above the ground it sits.
   *
   * A ping aimed at a rooftop belongs on the rooftop. Absent means the
   * floor, which is what open ground and every pre-v7.82.0 ping mean.
   */
  y?: number;
  /** Who sent it - the player, not the combatant. */
  by: string;
  byName: string;
  /** Stamped by the SENDER: when it was said (the log's time). Not what a screen counts its hold from: `seenAt`. */
  at: number;
  /**
   * THIS SCREEN's clock when it first held the ping, its hold counted from here; local, never written (2026-10-31).
   * Counted on the sender's clock instead, a ping 300-600 ms on the wire - or from a laptop whose clock ran half a
   * second behind - arrived spent and was never seen. Absent: `at` (a ping made before this field).
   */
  seenAt?: number;
  /** What it landed on and how far away, worked out once by the sender. */
  note?: string;
  /** For `going`: the token the arrow is drawn from. */
  fromId?: string;
}

/**
 * How long a ping is on screen. HALF A SECOND, and that is deliberate.
 *
 * Six seconds was tried and then three: both read as stuck. Once the ring
 * has opened there is nothing left to watch, and a mark that lingers on a
 * tactical map looks like something the GM placed rather than something
 * somebody said. A ping is a gesture - the pointed finger, not the note
 * pinned to the board.
 *
 * What makes it durable is the LOG, not the ring: the entry stays, and
 * clicking it takes you there.
 */
export const PING_TTL_MS = 600;

/** No two pings from one player inside this. The ping-spam problem. */
const PING_COOLDOWN_MS = 1000;

/**
 * The four things worth saying. In wheel order - up, right, down, left.
 *
 * Deliberately small. A vocabulary nobody can remember is a vocabulary
 * nobody uses, and the log entry carries any nuance the wheel cannot.
 */
export const PING_TYPES: Record<PingType, { label: string; colour: string; hint: string }> = {
  look: { label: 'Look here', colour: '#60a5fa', hint: 'Attention on this spot' },
  danger: { label: 'Danger', colour: '#ef4444', hint: 'Something here can hurt us' },
  going: { label: 'Going here', colour: '#22c55e', hint: 'My intent - no movement is spent' },
  question: { label: 'What is this?', colour: '#fbbf24', hint: 'A question for the GM' },
};

/** Wheel order, which `wheelChoice` and the wheel component share. */
const WHEEL_ORDER: PingType[] = ['look', 'danger', 'going', 'question'];

/** Inside this many pixels of the press point, the wheel means "cancel". */
const WHEEL_DEAD_ZONE = 14;

let pingCounter = 0;
/** Monotonic, never Math.random() - the same shape as `nextZoneId`. */
export function nextPingId(): string {
  pingCounter += 1;
  return `ping-${Date.now().toString(36)}-${pingCounter.toString(36)}`;
}

export interface PingSeed {
  type: PingType;
  x: number;
  z: number;
  y?: number;
  by: string;
  byName: string;
  at: number;
  note?: string;
  fromId?: string;
}

/**
 * Build one, with no `undefined` anywhere in it.
 *
 * Firestore rejects a document outright if any field is literally
 * `undefined` - and the write fails async and only logs, so the ping
 * would simply never appear (gotchas, v6.45.0).
 */
export function makePing(seed: PingSeed): Ping {
  return {
    id: nextPingId(),
    kind: 'ping',
    type: seed.type,
    x: seed.x,
    z: seed.z,
    ...(seed.y ? { y: seed.y } : {}),
    by: seed.by,
    byName: seed.byName,
    at: seed.at,
    ...(seed.note ? { note: seed.note } : {}),
    ...(seed.fromId ? { fromId: seed.fromId } : {}),
  };
}

/** When this screen's hold on the ping began (`seenAt`, else the sender's stamp). */
export const bornAt = (p: Ping): number => p.seenAt ?? p.at;

/**
 * A ping older than this by its SENDER's clock when it first arrives is not shown: a joiner reading the collection
 * must not be greeted by what was said a while ago. Generous, so latency and clock skew never hide a live one.
 */
export const PING_ARRIVAL_STALE_MS = 10_000;

/**
 * How long a screen HOLDS a ping - longer than it shows it. The half-second on screen (`PING_TTL_MS`) is an animation
 * the browser starts when the ring is first PAINTED (2026-10-31): counted from the press instead, a laptop slow to
 * render spent it before the ring reached the screen, and its player never saw their own ping. Held a few seconds,
 * the slowest paint still plays the whole fade; once played, the mark sits invisible until it is dropped.
 */
export const PING_HELD_MS = 3000;

/**
 * May this player ping yet?
 *
 * A clock that went backwards must not lock somebody out until it
 * catches up, so anything in the future counts as "long enough ago".
 */
export function canPing(lastAt: number | null | undefined, now: number): boolean {
  if (lastAt == null) return true;
  if (lastAt > now) return true;
  return now - lastAt >= PING_COOLDOWN_MS;
}

/**
 * Which wedge a drag from the press point lands in.
 *
 * Null near the centre: releasing where you pressed is how you change
 * your mind, and it must not be possible to pick a ping by accident
 * while doing it.
 *
 * Screen coordinates, so `dy` grows DOWNWARD - which is why 'look' is
 * negative dy.
 */
export function wheelChoice(dx: number, dy: number): PingType | null {
  if (Math.hypot(dx, dy) < WHEEL_DEAD_ZONE) return null;
  // Quarter turns from straight up, clockwise: up, right, down, left.
  const angle = Math.atan2(dx, -dy);
  const quarter = Math.round(angle / (Math.PI / 2));
  return WHEEL_ORDER[((quarter % 4) + 4) % 4];
}

export interface EdgeArrow {
  x: number;
  y: number;
  /** Radians, 0 pointing right - the direction the ping lies in. */
  angle: number;
}

/**
 * Where to draw an arrow for a ping nobody can see.
 *
 * Every client has its own camera, so "look here" is worth nothing if
 * "here" is off the side of somebody's screen. Returns null when the
 * ping is already in view and needs no help.
 */
export function edgeArrow(p: { x: number; y: number }, view: Viewport, margin = 24): EdgeArrow | null {
  const inside = p.x >= 0 && p.x <= view.width && p.y >= 0 && p.y <= view.height;
  if (inside) return null;

  const cx = view.width / 2;
  const cy = view.height / 2;
  const angle = Math.atan2(p.y - cy, p.x - cx);

  return {
    x: Math.min(Math.max(p.x, margin), view.width - margin),
    y: Math.min(Math.max(p.y, margin), view.height - margin),
    angle,
  };
}
